import React from "react"
import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Card, Container, Row, Col, Button, Form, Alert } from "react-bootstrap"
import { FaShieldAlt, FaLock, FaEye, FaEyeSlash, FaUserCheck, FaGoogle, FaApple, FaCrown } from "react-icons/fa"
import { useAuth } from "../../context/AuthContext"
import api from "../../services/api"


export default function SuperAdminLogin() {
  const { loginSuperAdmin } = useAuth();
  const navigate = useNavigate()
  const { login } = useAuth()
  const [showAlert, setShowAlert] = useState({ show: false, variant: "", message: "" })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.email || !formData.password) {
      setShowAlert({ show: true, variant: "danger", message: "Please enter valid super admin credentials." })
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
      return
    }

    setLoading(true)
    try {
      // Use the super admin login endpoint
      const response = await loginSuperAdmin(formData.email, formData.password);
      // const response = await api.loginSuperAdmin(formData.email, formData.password)
      
      if (response.success) {
        setShowAlert({
          show: true,
          variant: "success",
          message: "Super Admin Login Successful! Welcome to the super admin dashboard.",
        })
        setTimeout(() => {
          navigate("/admin/super-admin")
        }, 1500)
      } else {
        setShowAlert({ show: true, variant: "danger", message: response.message })
      }
    } catch (error) {
      setShowAlert({ show: true, variant: "danger", message: "Login failed. Please try again." })
    } finally {
      setLoading(false)
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <div
      className="min-vh-100 d-flex align-items-center position-relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
      }}
    >
      {/* Animated Background Elements */}
      <div className="position-absolute w-100 h-100">
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "200px",
            height: "200px",
            background: "white",
            top: "10%",
            right: "15%",
            animation: "float 6s ease-in-out infinite",
          }}
        ></div>
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "150px",
            height: "150px",
            background: "white",
            bottom: "15%",
            left: "10%",
            animation: "float 8s ease-in-out infinite reverse",
          }}
        ></div>
      </div>

      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            {/* Header */}
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{
                  width: "80px",
                  height: "80px",
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  boxShadow: "0 8px 32px rgba(220, 38, 38, 0.3)",
                }}
              >
                <FaCrown className="text-white" size={32} />
              </div>
              <h2 className="text-white fw-bold mb-2">Super Admin Portal</h2>
              <p className="text-white-50 mb-0">Complete system control and management</p>
            </div>

            {/* Login Card */}
            <Card className="border-0 shadow-lg">
              <Card.Body className="p-4">
                {showAlert.show && (
                  <Alert variant={showAlert.variant} dismissible onClose={() => setShowAlert({ show: false, variant: "", message: "" })}>
                    {showAlert.message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  {/* Email Field */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold text-muted">
                      <FaUserCheck className="me-2" />
                      Super Admin Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your super admin email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="border-2"
                      style={{ borderColor: "#e5e7eb" }}
                      required
                    />
                  </Form.Group>

                  {/* Password Field */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-muted">
                      <FaLock className="me-2" />
                      Password
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        className="border-2 pe-5"
                        style={{ borderColor: "#e5e7eb" }}
                        required
                      />
                      <Button
                        type="button"
                        variant="link"
                        className="position-absolute end-0 top-0 h-100 text-muted"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ zIndex: 10 }}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                    </div>
                  </Form.Group>

                  {/* Remember Me */}
                  <Form.Group className="mb-4">
                    <Form.Check
                      type="checkbox"
                      label="Remember me"
                      checked={formData.rememberMe}
                      onChange={(e) => handleInputChange("rememberMe", e.target.checked)}
                    />
                  </Form.Group>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    className="w-100 mb-3 fw-semibold py-2"
                    disabled={loading}
                    style={{
                      background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                      border: "none",
                      boxShadow: "0 4px 16px rgba(220, 38, 38, 0.3)",
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Logging in...
                      </>
                    ) : (
                      <>
                        <FaShieldAlt className="me-2" />
                        Login as Super Admin
                      </>
                    )}
                  </Button>

                  {/* Divider */}
                  <div className="text-center mb-3">
                    <span className="text-muted">or</span>
                  </div>

                  {/* Social Login Buttons */}
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-dark"
                      className="fw-semibold py-2"
                      disabled
                    >
                      <FaGoogle className="me-2" />
                      Continue with Google
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="fw-semibold py-2"
                      disabled
                    >
                      <FaApple className="me-2" />
                      Continue with Apple
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Footer Links */}
            <div className="text-center mt-4">
              <p className="text-white-50 mb-2">
                Don't have super admin access?{" "}
                <Link to="/admin/login" className="text-white text-decoration-none fw-semibold">
                  Contact System Administrator
                </Link>
              </p>
              <p className="text-white-50 mb-0">
                <Link to="/" className="text-white-50 text-decoration-none">
                  ← Back to Home
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </Container>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  )
}
